import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'

import {
  MagicIcon,
  PlusIcon,
  RectangleIcon,
  TextIcon,
  UploadIcon,
} from '@/components/ui/icons/icons-new'

import { useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'

import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/shadcn/popover'

import { ChevronDown, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTheme } from '@/context/themeContext'
import { DownloadIconNew } from '@/components/ui/icons/icons-new/download-icon-new'
import { ColorPickerQuickmenu } from '@/components/ui/ColorPicker'
import { colorToString } from '@/components/modules/CanvasEditor/color'
import { type Color } from '@backend/lib/db/typeRoots/color'

interface DropdownProps<T extends { key: string; label: string; icon?: React.ReactNode }> {
  trigger: React.ReactNode
  options: T[]
  onClick: (key: T['key']) => void
  hoverContent?: string
  showChevron?: boolean
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  offset?: number
  title?: string
  /** Optional custom content rendered below the options (e.g. a custom input). */
  footer?: React.ReactNode
}

export function DropdownItemMenu<T extends { key: string; label: string; icon?: React.ReactNode }>({
  trigger,
  options,
  onClick,
  hoverContent,
  showChevron = true,
  side = 'bottom',
  align = 'start',
  offset = 0,
  title,
  footer,
}: DropdownProps<T>) {
  const { theme } = useTheme()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [justClosed, setJustClosed] = useState(false)
  const hasIcons = options.some((option) => option.icon)

  const handleOpenChange = (open: boolean) => {
    setIsDropdownOpen(open)
    if (!open) {
      setJustClosed(true)
      setTimeout(() => setJustClosed(false), 500)
    }
  }

  const dropdownTrigger = (
    <DropdownMenuTrigger
      className={cn(
        'text-foreground hover:bg-hover flex h-10 w-10 items-center justify-center rounded-full text-center font-light',
        isDropdownOpen && 'bg-hover',
      )}>
      {trigger}
      {showChevron && <ChevronDown className="size-4 shrink-0" />}
    </DropdownMenuTrigger>
  )

  const content = hoverContent ? (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={isDropdownOpen || justClosed ? false : undefined}>
        <TooltipTrigger asChild>{dropdownTrigger}</TooltipTrigger>
        <TooltipContent side={side} align="center" sideOffset={10}>
          <p>{hoverContent}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    dropdownTrigger
  )

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={handleOpenChange}>
      {content}
      <DropdownMenuContent
        side={side}
        align={align}
        sideOffset={offset}
        className={cn('flex flex-col gap-2 p-2', theme === 'dark' ? 'dark' : '')}>
        {title && <p className="font-regular text-muted-foreground text-sm">{title}</p>}
        <div className={cn('', hasIcons ? 'flex gap-2' : '')}>
          {options.map(({ key, label, icon }) => (
            <DropdownMenuItem
              key={key}
              onClick={() => onClick(key)}
              className="font-regular focus:bg-hover text-sm">
              {icon ? icon : label}
            </DropdownMenuItem>
          ))}
        </div>
        {footer}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function DropdownItemMenu2<T extends { key: string; label: string }>({
  trigger,
  options,
  onClick,
  hoverContent,
  showChevron = true,
  side = 'bottom',
  align = 'start',
  offset = 0,
  title,
}: DropdownProps<T>) {
  const { theme } = useTheme()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [justClosed, setJustClosed] = useState(false)

  const handleOpenChange = (open: boolean) => {
    setIsDropdownOpen(open)
    if (!open) {
      setJustClosed(true)
      setTimeout(() => setJustClosed(false), 500)
    }
  }

  const dropdownTrigger = (
    <DropdownMenuTrigger className="text-foreground bg-primary flex h-10 w-10 items-center justify-center rounded-full">
      {trigger}
      {showChevron && <ChevronDown className="size-4 shrink-0" />}
    </DropdownMenuTrigger>
  )

  const content = hoverContent ? (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={isDropdownOpen || justClosed ? false : undefined}>
        <TooltipTrigger asChild>{dropdownTrigger}</TooltipTrigger>
        <TooltipContent side={side} align="center" sideOffset={10}>
          <p>{hoverContent}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    dropdownTrigger
  )

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={handleOpenChange}>
      {content}
      <DropdownMenuContent
        side={side}
        align={align}
        sideOffset={offset}
        className={cn('flex flex-col gap-2 p-2', theme === 'dark' ? 'dark' : '')}>
        {title && <p className="text-muted-foreground font-regular text-sm">{title}</p>}
        {options.map(({ key, label }) => (
          <DropdownMenuItem
            key={key}
            onClick={() => onClick(key)}
            className={cn(
              'font-regular focus:bg-hover h-10 w-36 items-center justify-center rounded-full border text-sm',
              key === 'generate'
                ? 'bg-primary focus:bg-primary-hover text-primary-foreground'
                : 'bg-card border-foreground focus:bg-hover',
            )}>
            {key === 'upload' && <UploadIcon width={22} height={22} color="var(--foreground)" />}
            {key === 'generate' && <MagicIcon width={22} height={22} color="white" />}
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface MenuItem<T extends { key: string; label: string }> {
  options: T[]
  onClick: (key: T['key']) => void
}

interface Props {
  imageItems: MenuItem<{ key: 'upload' | 'generate' | 'logo'; label: string }>
  shapeItems: MenuItem<{
    key: 'rectangle' | 'circle' | 'triangle'
    label: string
    icon?: React.ReactNode
  }>
  aspectRatioItems: MenuItem<{ key: string; label: string }> & {
    currentAspectRatio: string
    footer?: React.ReactNode
  }
  backgroundColor: Color
  onBackgroundColorChange: (color: Color) => void
  colorPalette: string[]
  onTextAdd: () => void
  onDownload: () => void
  overlayOpacity: number
  onOverlayOpacityChange: (opacity: number) => void
  hidden?: boolean
}

export function WorkspaceEditorMenuGeneral({
  imageItems,
  shapeItems,
  aspectRatioItems,
  backgroundColor,
  onBackgroundColorChange,
  colorPalette,
  onTextAdd,
  onDownload,
  overlayOpacity,
  onOverlayOpacityChange,
  hidden = false,
}: Props) {
  const { theme } = useTheme()
  const [isBgColorOpen, setIsBgColorOpen] = useState(false)
  const [justClosedBgColor, setJustClosedBgColor] = useState(false)
  return (
    <div
      className={cn(
        'fixed left-4 top-1/2 z-50 -translate-y-[calc(50%+50px)] transition-all duration-300 ease-in-out',
        hidden ? '-translate-x-20 opacity-0' : 'translate-x-0 opacity-100',
      )}>
      <div className="border-border bg-card shadow-xs flex flex-col gap-2 rounded-full border px-1 py-1.5">
        <DropdownItemMenu2
          trigger={<PlusIcon width={22} height={22} color="white" />}
          options={imageItems.options}
          onClick={imageItems.onClick}
          title="Add to canvas"
          hoverContent="Add post image"
          showChevron={false}
          side="right"
          align="start"
          offset={15}
        />

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <span
                className="hover:bg-hover flex h-10 w-10 items-center justify-center rounded-full"
                onClick={onTextAdd}>
                <TextIcon width={22} height={22} color="var(--foreground)" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="right" align="center" sideOffset={10}>
              <p>Add text</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <DropdownItemMenu
          trigger={<RectangleIcon width={22} height={22} color="var(--foreground)" />}
          options={shapeItems.options}
          onClick={shapeItems.onClick}
          title="Shapes"
          hoverContent="Add shape"
          showChevron={false}
          side="right"
          align="start"
          offset={15}
        />

        <DropdownItemMenu
          trigger={aspectRatioItems.currentAspectRatio}
          options={aspectRatioItems.options}
          onClick={aspectRatioItems.onClick}
          title="Aspect ratio"
          hoverContent="Set aspect ratio"
          showChevron={false}
          side="right"
          align="start"
          offset={15}
          footer={aspectRatioItems.footer}
        />

        <Popover
          open={isBgColorOpen}
          onOpenChange={(open) => {
            setIsBgColorOpen(open)
            if (!open) {
              setJustClosedBgColor(true)
              setTimeout(() => setJustClosedBgColor(false), 500)
            }
          }}>
          <TooltipProvider delayDuration={300}>
            <Tooltip open={isBgColorOpen || justClosedBgColor ? false : undefined}>
              <TooltipTrigger asChild>
                <PopoverTrigger
                  className={cn(
                    'hover:bg-hover flex h-10 w-10 items-center justify-center rounded-full',
                    isBgColorOpen && 'bg-hover',
                  )}>
                  <div
                    className="border-border h-5 w-5 rounded-full border"
                    style={{ backgroundColor: colorToString(backgroundColor, 'hex').slice(0, 7) }}
                  />
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="right" align="center" sideOffset={10}>
                <p>Background color</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <PopoverContent
            className={cn(
              'border-border shadow-xs w-auto border p-3',
              theme === 'dark' ? 'dark' : '',
            )}
            side="right"
            align="start"
            sideOffset={15}>
            <ColorPickerQuickmenu
              value={backgroundColor}
              onChange={onBackgroundColorChange}
              colorPalette={colorPalette}
            />
          </PopoverContent>
        </Popover>

        <Popover>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger
                  className={cn(
                    'hover:bg-hover flex h-10 w-10 items-center justify-center rounded-full',
                    overlayOpacity > 0.12 && 'bg-hover',
                  )}>
                  {overlayOpacity > 0.12 ? (
                    <EyeOff className="size-[22px] text-[var(--foreground)]" />
                  ) : (
                    <Eye className="size-[22px] text-[var(--foreground)]" />
                  )}
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="right" align="center" sideOffset={10}>
                <p>Overflow visibility</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <PopoverContent
            className={cn(
              'border-border shadow-xs w-auto border p-3',
              theme === 'dark' ? 'dark' : '',
            )}
            side="right"
            align="start"
            sideOffset={15}>
            <div className="flex w-36 flex-col gap-2">
              <p className="text-muted-foreground text-xs font-medium">Dim overflow</p>
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(overlayOpacity * 100)}
                onChange={(e) => onOverlayOpacityChange(Number(e.target.value) / 100)}
                className="w-full accent-[var(--primary)]"
              />
            </div>
          </PopoverContent>
        </Popover>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <span
                className="hover:bg-hover flex h-10 w-10 items-center justify-center rounded-full"
                onClick={onDownload}>
                <DownloadIconNew width={22} height={22} color="var(--foreground)" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="right" align="center" sideOffset={10}>
              <p>Download</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  )
}
